import { useMemo } from 'react'
import * as React from 'react'
import styled, { css } from 'styled-components'
import clsx from 'clsx'
import type { PolymorphicComponentProps, StyledTarget } from 'styled-components/dist/types'

// Can add more sizes here if needed
const containerSizes = [640] as const

type ContainerSize = typeof containerSizes[number]

const precompiledCss = containerSizes.map(size => css`
  &.hide-min-${size} {
    @container (min-width: ${size}px) {
      display: none;
    }
  }

  &.hide-max-${size} {
    @container (max-width: ${size - 1}px) {
      display: none;
    }
  }
`)

const StyledContainer = styled.div`
  ${precompiledCss}

  &:not(.show-when-empty) {
    &:empty:not(:is(br, hr, input, textarea)) {
      display: none;
    }
  }
`

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  min?: ContainerSize
  max?: ContainerSize
  showWhenEmpty?: boolean
}

/*
* https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_queries
* When using this util, make sure to set the container-type to inline-size | size | normal
* on the parent element that you want to query the size of.
*/
function ContainerBreakpoint<AsTarget extends StyledTarget<'web'> = 'div'>(props: PolymorphicComponentProps<'web', Props, AsTarget, void>) {
  const { min, max, showWhenEmpty = false, className, children, ...rest } = props

  const classes = useMemo(() => {
    return clsx(className, {
      [`hide-max-${min}`]: min,
      [`hide-min-${max}`]: max,
      'show-when-empty': showWhenEmpty,
    })
  }, [min, max, showWhenEmpty, className])

  return (
    <StyledContainer {...rest} className={classes}>
      {children}
    </StyledContainer>
  )
}

export default ContainerBreakpoint
